// 📁 config/productsConfig.js
//
// Zezamii is a suite of 3 products. This config declares them for the
// Products page (Settings > Products) and for any other place that needs
// to reason about "which products does this org have".
//
// Each product has a moduleFlag (DefaultSettingID) that lives in the
// Setting table. When the flag is ON for an org:
//   - The product appears in the dashboard switcher
//   - Its settings tab(s) become visible in the left menu
//   - Its routes become accessible
//
// To add a 4th product in the future:
//   1. Create the DefaultSetting row in DB (SQL migration).
//   2. Add the constant in constants/settings.js.
//   3. Add an entry here.

import openRooms from 'library/helpers/openRooms';
import { CONSTANTS_SETTINGS } from './settingsContainer';
import { LogoAccessApp, LogoLockerApp, LogoRoomsApp } from 'assets/images/svg/Logos';

/**
 * @typedef {Object} ProductConfig
 * @property {string}   key              - Stable identifier (also the value stored in state.App.dashboard.selected)
 * @property {string}   label            - Display name in UI (e.g. "Zezamii Access")
 * @property {string}   description      - Short pitch shown on cards
 * @property {JSX}      logo             - Rendered icon/logo element
 * @property {number}   moduleFlag       - DefaultSettingID that toggles this product
 * @property {'internal'|'external'} kind
 *   - 'internal': lives inside this SPA — switching navigates via react-router
 *   - 'external': lives at a different URL — switching invokes `open()` instead
 * @property {string}   [route]          - Required when kind === 'internal'
 * @property {() => void} [open]         - Required when kind === 'external'
 */

/** @type {ProductConfig[]} */
export const PRODUCTS = [
	{
		key: 'access',
		label: 'Zezamii Access',
		description:
			'Manage access across every lock, door, and credential from one place. Zezamii Access unifies your hardware, people, and permissions — and lets anyone unlock from the mobile app.',
		logo: <LogoAccessApp />,
		moduleFlag: CONSTANTS_SETTINGS.ACCESS_MODULE,
		kind: 'internal',
		route: '/access'
	},
	{
		key: 'lockers',
		label: 'Zezamii Lockers',
		description:
			'Manage shared locker bookings across every building from one place. Zezamii Lockers unifies booking rules, usage metrics, and access logs — and lets anyone book and unlock from the mobile app.',
		logo: <LogoLockerApp />,
		moduleFlag: CONSTANTS_SETTINGS.LOCKER_MODULE,
		kind: 'internal',
		route: '/lockers'
	},
	{
		key: 'rooms',
		label: 'Zezamii Rooms',
		description:
			'Manage guest access across every room and PMS from one place. Zezamii Rooms unifies PMS bookings, lock integrations, and passcode delivery — and lets every guest unlock with a code tied to their reservation.',
		logo: <LogoRoomsApp />,
		moduleFlag: CONSTANTS_SETTINGS.ROOMS_MODULE,
		kind: 'external',
		open: openRooms
	}
];

// Rule enforced in the Products UI: at least one product must stay on.
// A workspace with zero products is a broken workspace.
export const MIN_ACTIVE_PRODUCTS = 1;
